import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './DropZone.scss';

const getFileExtensions = (filename) => {
  const exts = filename.split('.');
  if (exts.length < 4) return exts;
  return [exts.slice(0, exts.length - 2), exts[exts.length - 2], exts[exts.length - 1]];
};

class DropZone extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    onFilesAdded: PropTypes.func.isRequired,
    validExtensions: PropTypes.arrayOf(PropTypes.string),
    onAcceptFiles: PropTypes.func,
    errorDisplayTimeout: PropTypes.number,
    invalidExtensionMessage: PropTypes.string,
  };

  static defaultProps = {
    disabled: false,
    validExtensions: undefined,
    onAcceptFiles: undefined,
    errorDisplayTimeout: 4000,
    invalidExtensionMessage: undefined,
  };

  constructor(props) {
    super(props);
    this.state = { hightlight: false, error: false };
    this.fileInputRef = React.createRef();

    this.handleOpenFileDialog = this.handleOpenFileDialog.bind(this);
    this.handleFilesAdded = this.handleFilesAdded.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
  }

  handleOpenFileDialog() {
    if (!this.props.disabled) {
      this.fileInputRef.current.click();
    }
  }

  handleFilesAdded(evt) {
    this.addFileList(evt.target.files);
    // https://github.com/ngokevin/react-file-reader-input/issues/11
    evt.target.value = '';
  }

  validateFiles(fileList) {
    const { validExtensions, onAcceptFiles, invalidExtensionMessage } = this.props;
    const files = this.fileListToArray(fileList);
    if (onAcceptFiles) {
      return onAcceptFiles(files);
    }
    const isInvalidFile = (file) => {
      const [, ext, cext] = getFileExtensions(file.name);
      return validExtensions && (validExtensions.indexOf(cext ? `${ext}.${cext}` : ext) < 0);
    };
    if (files.find(isInvalidFile)) {
      return invalidExtensionMessage
          || `Invalid file extension(s).\nAccepted files are ${validExtensions.join(', ')}`;
    }
    return null;
  }

  handleDragOver(event) {
    event.stopPropagation();
    event.preventDefault();

    if (!this.props.disabled) {
      const { timerId } = this.state;
      if (timerId !== undefined) {
        clearTimeout(timerId);
        this.setState({ hightlight: true, timerId: undefined, error: undefined });
      } else {
        this.setState({ hightlight: true });
      }
    }
  }

  handleDragLeave() {
    this.setState({ hightlight: false });
  }

  handleDrop(event) {
    event.stopPropagation();
    event.preventDefault();

    this.addFileList(event.dataTransfer.files);
    this.setState({ hightlight: false });
  }

  fileListToArray(list) {
    const array = [];
    for (let i = 0; i < list.length; i += 1) {
      array.push(list.item(i));
    }
    return array;
  }

  addFileList(files) {
    if (!this.props.disabled) {
      const error = this.validateFiles(files);
      if (this.props.onFilesAdded && !error) {
        const array = this.fileListToArray(files);
        this.props.onFilesAdded(array);
      } else if (error) {
        this.setState({
          error,
          timerId: setTimeout(() => (this.setState({ timerId: undefined, error: undefined })), this.props.errorDisplayTimeout),
        });
      }
    }
  }

  render() {
    const { validExtensions } = this.props;
    // The HTML input accept entry does not accept double extensions such as .mps.zip
    // As a workaround, we accept all .zip and be filtering only .mps.zip in validateFiles
    // after the file has been selecte in the File chooser.
    let fixedValidExtensions;
    if (validExtensions) {
      const doubleExts = {};
      fixedValidExtensions = validExtensions.filter((validExt) => {
        const index = validExt.indexOf('.');
        if (index >= 0) {
          doubleExts[validExt.substring(index + 1)] = true;
          return false;
        }
        return true;
      });
      fixedValidExtensions = fixedValidExtensions.concat(Object.keys(doubleExts));
    }
    const { hightlight, error } = this.state;
    /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    return (
      <div
        className={classnames('grb-dropzone', hightlight && 'grb-dropzone-highlight', error && 'grb-dropzone-error')}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
        onClick={this.handleOpenFileDialog}
        style={{ cursor: this.props.disabled ? 'default' : 'pointer' }}
      >
        <input
          ref={this.fileInputRef}
          className="FileInput"
          accept={fixedValidExtensions ? fixedValidExtensions.map(ext => `.${ext}`).join(',') : undefined}
          type="file"
          multiple
          onChange={this.handleFilesAdded}
        />
        <span>{error || 'Drag files or click to browse'}</span>
      </div>
    );
    /* eslint-enable jsx-a11y/no-static-element-interactions */
  }
}

export default DropZone;
